import _ from 'underscore';

import AbstractSearch from '@/js/common/Domain/Service/search/AbstractSearch';
import populateFPD from '@/js/common/Domain/Service/hotelOffersSearch/populateFPD';
import tourCriteriaHotelOffersJsonNewApi from '@/js/common/Domain/Hydrator/tourCriteriaHotelOffersJsonNewApi';
import asyncTourCriteriaHotelOffersJson from '@/js/common/Domain/Hydrator/asyncTourCriteriaHotelOffersJson';
import { rawProductCompareKey, productCompareKey } from '@/js/common/Domain/Service/search/getCompareKeys';
import HotelsRepository from '../Repository/Hotels';
import HotelFactory from '../Factory/Hotel';
import HotelOffersRepository from '../Repository/HotelOffers';
import hotelOffersSession from './hotelOffersSession';
import SessionStatusService from './SessionStatus';
import hotelOffersSearchCache from './hotelOffersSearchCache';
import HotelOfferFactory from '../Factory/HotelOfferFactory';

import getTourFirstPaymentDefinitionService from './getTourFirstPaymentDefinition';

export default class HotelOffersSearch extends AbstractSearch {
    constructor(options = {}) {
        super();
        this.hotelsRepositorySync = new HotelsRepository();
        this.hotelOffersRepositorySync = new HotelOffersRepository();
        this.HotelFactory = options.HotelFactory || HotelFactory;
    }

    getOffersData(getRequestOptions) {
        const {
            searchUuid,
            clientUuid,
            customerUuid,
            requests,
            callback,
            urls,
        } = getRequestOptions;

        this.reset();

        this.startSearchTime = (new Date()).getTime();

        _.each(requests, (request) => {
            const cacheAjaxData = tourCriteriaHotelOffersJsonNewApi(request.tourCriteria);
            const asyncAjaxData = asyncTourCriteriaHotelOffersJson(request.tourCriteria);

            const afterFirstSearchRequest = {
                success: false,
            };
            let trackThisRequest = false;

            cacheAjaxData.commandUuid = searchUuid;
            asyncAjaxData.commandUuid = searchUuid;
            cacheAjaxData.clientUuid = clientUuid;
            asyncAjaxData.clientUuid = clientUuid;
            cacheAjaxData.customerUuid = customerUuid;
            asyncAjaxData.customerUuid = customerUuid;

            if (cacheAjaxData.track) {
                trackThisRequest = true;
                delete (cacheAjaxData.track);
            }

            if (request.limit) {
                cacheAjaxData.limit = request.limit;
            }

            if (request.type === 'sync' || request.type === 'both' || typeof request.type === 'undefined') {
                const syncRequest = () => {
                    const XHR = hotelOffersSearchCache({
                        ...cacheAjaxData,
                        limit: cacheAjaxData.limit || 2000,
                    }, {
                        success: (response) => {
                            if (_.isFunction(afterFirstSearchRequest.callback)) {
                                afterFirstSearchRequest.callback();
                                afterFirstSearchRequest.success = true;
                                afterFirstSearchRequest.callback = false;
                            } else {
                                afterFirstSearchRequest.success = true;
                            }

                            this.updateData(response.result, request.tourCriteria, {
                                hotels: this.hotelsRepositorySync,
                                hotelOffers: this.hotelOffersRepositorySync,
                            });

                            if (this.startSearchTime && this.hotelsRepositorySync.length) {
                                this.firstCacheTime = (new Date()).getTime();
                            }

                            if (this.allAjaxAreCompleted('sync')) {
                                callback.sync(this.hotelsRepositorySync, this.hotelOffersRepositorySync);
                            }
                        },
                        error: (XMLHttpRequest, textStatus, errorThrown) => {
                            if (errorThrown !== 'abort') {
                                if (_.isFunction(afterFirstSearchRequest.callback)) {
                                    afterFirstSearchRequest.callback();
                                    afterFirstSearchRequest.success = true;
                                    afterFirstSearchRequest.callback = false;
                                } else {
                                    afterFirstSearchRequest.success = true;
                                }

                                if (this.allAjaxAreCompleted('sync')) {
                                    callback.sync(this.hotelsRepositorySync, this.hotelOffersRepositorySync);
                                }
                            }
                        },
                    }, urls && urls.searchCache ? urls.searchCache : null);

                    this.syncAjaxRequests.push(XHR);
                    this.ajaxRequests.push(XHR);
                };

                if (typeof (request.timeout) !== 'undefined') {
                    this.requestTimeouts.push(setTimeout(() => {
                        if (this.hotelsRepositorySync?.length === 0) {
                            syncRequest();
                        }
                    }, request.timeout));
                } else {
                    syncRequest();
                }
            }
            if (request.type === 'async' || request.type === 'both' || typeof request.type === 'undefined') {
                if (request.type === 'async') {
                    afterFirstSearchRequest.success = true;
                }

                this.getOffersDataAsync({ asyncAjaxData, cacheAjaxData }, {
                    success: (response) => {
                        const updatedHotelsData = this.updateData(response.result, request.tourCriteria, {
                            hotels: this.hotelsRepositorySync,
                            hotelOffers: this.hotelOffersRepositorySync,
                        });

                        if (this.startSearchTime) {
                            this.searchFinishedTime = (new Date()).getTime();
                        }

                        if (this.allAjaxAreCompleted('async')) {
                            callback.async(updatedHotelsData.updatedHotels, updatedHotelsData.newHotelsLength, {
                                startSearchTime: this.startSearchTime,
                                firstCacheTime: this.firstCacheTime,
                                searchFinishedTime: this.searchFinishedTime,
                                scannedTours: response.result?.hotelOffers?.length || 0,
                                error: 0,
                            });
                        }
                    },
                    middleRequest: (response) => {
                        const updatedHotelsData = this.updateData(response.result, request.tourCriteria, {
                            hotels: this.hotelsRepositorySync,
                            hotelOffers: this.hotelOffersRepositorySync,
                        });

                        if (this.startSearchTime && !this.firstCacheTime && this.hotelsRepositorySync.length) {
                            this.firstCacheTime = (new Date()).getTime();
                        }

                        if (callback && callback.middleRequest && updatedHotelsData) {
                            callback.middleRequest(updatedHotelsData.updatedHotels, updatedHotelsData.newHotelsLength);
                        }
                    },
                    error: (XMLHttpRequest, textStatus, errorThrown) => {
                        if (errorThrown !== 'abort') {
                            if (this.allAjaxAreCompleted('async')) {
                                if (this.startSearchTime) {
                                    this.searchFinishedTime = (new Date()).getTime();
                                }
                                callback.async([], 0, {
                                    startSearchTime: this.startSearchTime,
                                    firstCacheTime: this.firstCacheTime,
                                    searchFinishedTime: this.searchFinishedTime,
                                    scannedTours: 0,
                                    error: 1,
                                });
                            }
                        }
                    },
                }, trackThisRequest, urls || null, afterFirstSearchRequest);
            }
            if (request.type === 'asyncWithoutTours') {
                afterFirstSearchRequest.success = true;

                this.getAsyncWithoutHotelOffers({ asyncAjaxData, cacheAjaxData }, {
                    success(response) {
                        callback.success(response);
                    },
                    middleRequest() {
                    },
                    error: (XMLHttpRequest, textStatus, errorThrown) => {
                        if (errorThrown !== 'abort') {
                            this.allAjaxAreCompleted('async');
                        }
                        callback.error(XMLHttpRequest, textStatus, errorThrown);
                    },
                }, trackThisRequest, urls || null, afterFirstSearchRequest);
            }
        });
    }

    clearRepositories() {
        super.clearRepositories();
        this.hotelOffersRepositorySync.reset();
    }

    updateData(data, tourCriteria, repositories) {
        console.time('Hotels-HotelOffers loaded');

        const {
            firstPaymentDefinitions,
            hotels,
            hotelOffers,
        } = data;

        const country = tourCriteria.get('countries').at(0);
        const currentHotelsHashTable = repositories.hotels.pluck('id');
        const currentHotelOffersHashTable = repositories.hotelOffers.map(productCompareKey);
        const resorts = country.get('resorts');

        const updatedHotelsModels = [];

        console.log('Hotels from request:', hotels ? hotels.length : 0, 'HotelOffers from request:', hotelOffers?.length || 0);

        const populatedHotelOffers = populateFPD({
            hotelOffers,
            firstPaymentDefinitions,
        });

        if (hotels && hotelOffers) {
            console.time('Find new items');

            const newHotels = [];
            const newHotelOffers = [];

            _.each(hotels, (hotel) => {
                if (!~_.indexOf(currentHotelsHashTable, hotel.id)) {
                    newHotels.push(hotel);
                }
            });
            _.each(populatedHotelOffers, (hotelOffer) => {
                const rawProductKey = rawProductCompareKey(hotelOffer);
                if (!~_.indexOf(currentHotelOffersHashTable, rawProductKey)) {
                    const firstPaymentDefinition = getTourFirstPaymentDefinitionService(hotelOffer);
                    const allFirstPaymentDefinitions = hotelOffer?.firstPaymentDefinitions || [];

                    newHotelOffers.push({
                        ...hotelOffer,
                        firstPaymentDefinition,
                        allFirstPaymentDefinitions,
                    });
                }
            });

            console.timeEnd('Find new items');
            console.time('buildHotels');

            repositories.hotels.add(_.map(newHotels, (hotel) => new this.HotelFactory(hotel)), {
                merge: true,
            });

            console.timeEnd('buildHotels');

            console.time('buildHotelOffers');

            _.map(newHotelOffers, (hotelOffer) => $.extend(hotelOffer, {
                tourCriteria,
            }));

            const newHotelOffersModels = newHotelOffers.map((hotelOffer) => new HotelOfferFactory(hotelOffer, resorts));

            repositories.hotelOffers.add(newHotelOffersModels, {
                merge: true,
            });

            console.timeEnd('buildHotelOffers');
            console.time('new hotel offers to hotels');
            console.time('group hotelOffers');

            const newHotelOffersGroupByHotelId = _.groupBy(newHotelOffersModels, (hotelOffer) => hotelOffer.get('hotel'));
            console.timeEnd('group hotelOffers');
            console.time('hotelOffers merge with exist');

            _.each(newHotelOffersGroupByHotelId, (newHotelOffersByHotel, key) => {
                const hotel = repositories.hotels.get(parseInt(key, 10));

                if (hotel === undefined) {
                    return;
                }

                const oldHotelOffers = hotel.get('hotelOffers');
                let existHotelOffers = oldHotelOffers ? _.clone(oldHotelOffers.models) : [];

                updatedHotelsModels.push(hotel);
                existHotelOffers = existHotelOffers.concat(newHotelOffersByHotel);
                existHotelOffers = _.sortBy(existHotelOffers, (hotelOffer) => hotelOffer.get('price'));

                hotel.set('hotelOffers', new HotelOffersRepository(existHotelOffers));
            });

            console.timeEnd('hotelOffers merge with exist');
            console.timeEnd('new hotel offers to hotels');

            console.timeEnd('Hotels-HotelOffers loaded');
            console.log('New hotels', newHotels.length, 'New hotel offers', newHotelOffersModels.length, 'UpdatedHotels', updatedHotelsModels.length);

            return ({
                updatedHotels: updatedHotelsModels,
                newHotelsLength: newHotels.length,
            });
        }

        return null;
    }

    getOffersDataAsync(tourCriteriaForTourRequest, callback, trackThisRequest, urls, afterFirstSearchRequest) {
        const afterFirstSearchRequestInstance = afterFirstSearchRequest;
        const { cacheAjaxData, asyncAjaxData } = tourCriteriaForTourRequest;
        const { searchUuid, originalSearchUuid } = cacheAjaxData;

        const sessionXHR = hotelOffersSession(asyncAjaxData, {
            success: (response) => {
                const session = response?.result?.id || null;
                const getSessionWrapper = () => {
                    new SessionStatusService(session, {
                        success: () => {
                            if (trackThisRequest) {
                                cacheAjaxData.track = 1;
                            }
                            this.clearRequestTimeouts();

                            const XHR = hotelOffersSearchCache(cacheAjaxData, {
                                success(cacheResponse) {
                                    if (cacheResponse.success === true) {
                                        callback.success(cacheResponse);
                                    } else {
                                        callback.error(null, cacheResponse.message, cacheResponse);
                                    }
                                },
                                error: callback.error,
                            }, urls && urls.searchCache ? urls.searchCache : null);
                            this.asyncAjaxRequests.push(XHR);
                            this.ajaxRequests.push(XHR);
                        },
                        middleRequest: () => {
                            let XHR;
                            const middleRequestTourCriteria = _.clone(cacheAjaxData);

                            middleRequestTourCriteria.limit = 500;
                            if (callback && callback.middleRequest) {
                                XHR = hotelOffersSearchCache(middleRequestTourCriteria, {
                                    success(cacheResponse) {
                                        if (cacheResponse.success === true) {
                                            callback.middleRequest(cacheResponse);
                                        } else {
                                            callback.middleRequest({
                                                result: {
                                                    hotels: [],
                                                    hotelOffers: [],
                                                },
                                            });
                                        }
                                    },
                                    error() {
                                        callback.middleRequest({
                                            result: {
                                                hotels: [],
                                                hotelOffers: [],
                                            },
                                        });
                                    },
                                }, urls && urls.searchCache ? urls.searchCache : null);

                                this.ajaxRequests.push(XHR);
                            }
                            console.log('send middle request!!');
                        },
                        error: (message) => {
                            this.clearRequestTimeouts();
                            callback.error(null, message, response);
                        },
                    }, {
                        asyncAjaxRequests: this.asyncAjaxRequests,
                        ajaxRequests: this.ajaxRequests,
                        statusTimeouts: this.statusTimeouts,
                        globalStatusTimeouts: this.globalStatusTimeouts,
                        countryId: cacheAjaxData.country,
                        asyncStatus: this.asyncStatus,
                        searchUuid,
                        originalSearchUuid,
                    }, '/frontend/hotelOffers/asyncSearch/');
                };

                if (response.success === true && session) {
                    if (afterFirstSearchRequestInstance.success) {
                        getSessionWrapper();
                    } else {
                        afterFirstSearchRequestInstance.callback = getSessionWrapper;
                    }
                } else {
                    callback.error(null, response.message, response);
                }
            },
            error: callback.error,
        });

        this.ajaxRequests.push(sessionXHR);
        this.asyncAjaxRequests.push(sessionXHR);
    }

    getAsyncWithoutHotelOffers(tourCriteriaForTourRequest, callback, trackThisRequest, urls, afterFirstSearchRequest) {
        const afterFirstSearchRequestInstance = afterFirstSearchRequest;
        const { cacheAjaxData, asyncAjaxData } = tourCriteriaForTourRequest;
        const { searchUuid, originalSearchUuid } = cacheAjaxData;
        const sessionXHR = hotelOffersSession(asyncAjaxData, {
            success: (response) => {
                const session = response.result ? response.result.id : null;
                const getSessionWrapper = () => {
                    new SessionStatusService(session, {
                        success: () => {
                            if (trackThisRequest) {
                                cacheAjaxData.track = 1;
                            }
                            this.clearRequestTimeouts();
                            callback.success(response);
                        },
                        middleRequest() {
                        },
                        error: (message) => {
                            this.clearRequestTimeouts();
                            callback.error(null, message, response);
                        },
                    }, {
                        asyncAjaxRequests: this.asyncAjaxRequests,
                        ajaxRequests: this.ajaxRequests,
                        statusTimeouts: this.statusTimeouts,
                        globalStatusTimeouts: this.globalStatusTimeouts,
                        countryId: cacheAjaxData.country,
                        asyncStatus: this.asyncStatus,
                        searchUuid,
                        originalSearchUuid,
                    });
                };

                if (response.success === true && session) {
                    if (afterFirstSearchRequestInstance.success) {
                        getSessionWrapper();
                    } else {
                        afterFirstSearchRequestInstance.callback = getSessionWrapper;
                    }
                } else {
                    callback.error(null, response.message, response);
                }
            },
            error: callback.error,
        });

        this.ajaxRequests.push(sessionXHR);
        this.asyncAjaxRequests.push(sessionXHR);
    }
}